import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { LanguageService } from './language.service';
import { UuidService } from './uuid.service';

export interface ClientContext {
  clientType: string;
  deviceId: string;
  appVersion: string;
  screenResolution: string;
  os: string;
  osVersion: string;
  timezone: string;
  locale: string;
}

const STORAGE_KEY = 'fixz_client_context';

@Injectable({
  providedIn: 'root',
})
export class ClientContextService {
  private context: ClientContext | null = null;

  constructor(
    private uuidService: UuidService,
    private languageService: LanguageService
  ) {
    this.ensureContext();
  }

  /** Returns API client headers, extracting and persisting context when needed. */
  getApiHeaders(): Record<string, string> {
    const ctx = this.ensureContext();
    return {
      Accept: 'application/json',
      'X-Client-Type': ctx.clientType,
      'X-Device-Id': ctx.deviceId,
      'X-App-Version': ctx.appVersion,
      'X-Screen-Resolution': ctx.screenResolution,
      'X-OS': ctx.os,
      'X-OS-Version': ctx.osVersion,
      'X-Timezone': ctx.timezone,
      'X-Locale': this.languageService.current === 'ar' ? 'ar' : 'en',
    };
  }

  private ensureContext(): ClientContext {
    if (this.context) {
      return this.context;
    }

    const stored = this.loadFromStorage();
    if (stored && this.isComplete(stored)) {
      const currentVersion = (environment as { appVersion?: string }).appVersion || '1.0.0';
      if (stored.appVersion !== currentVersion) {
        stored.appVersion = currentVersion;
        this.saveToStorage(stored);
      }
      this.context = stored;
      return this.context;
    }

    this.context = this.extractContext();
    this.saveToStorage(this.context);
    return this.context;
  }

  private extractContext(): ClientContext {
    const { os, osVersion } = this.detectOs();
    const screen = typeof window !== 'undefined' ? window.screen : null;

    return {
      clientType: 'web',
      deviceId: this.uuidService.getUuid(),
      appVersion: (environment as { appVersion?: string }).appVersion || '1.0.0',
      screenResolution: screen
        ? `${screen.width}x${screen.height}`
        : 'unknown',
      os,
      osVersion,
      timezone: this.detectTimezone(),
      locale: this.languageService.current === 'ar' ? 'ar' : 'en',
    };
  }

  private detectTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }

  private detectOs(): { os: string; osVersion: string } {
    if (typeof navigator === 'undefined') {
      return { os: 'Unknown', osVersion: 'Unknown' };
    }

    const ua = navigator.userAgent;

    const windowsMatch = ua.match(/Windows NT ([\d.]+)/);
    if (windowsMatch) {
      const ntVersion = windowsMatch[1];
      const osVersion =
        ntVersion === '10.0' ? '11' : ntVersion === '6.3' ? '8.1' : ntVersion;
      return { os: 'Windows', osVersion };
    }

    const macMatch = ua.match(/Mac OS X ([\d_]+)/);
    if (macMatch) {
      return {
        os: 'macOS',
        osVersion: macMatch[1].replace(/_/g, '.'),
      };
    }

    const androidMatch = ua.match(/Android ([\d.]+)/);
    if (androidMatch) {
      return { os: 'Android', osVersion: androidMatch[1] };
    }

    const iosMatch = ua.match(/OS ([\d_]+) like Mac OS X/);
    if (iosMatch) {
      return { os: 'iOS', osVersion: iosMatch[1].replace(/_/g, '.') };
    }

    if (/Linux/.test(ua)) {
      return { os: 'Linux', osVersion: 'Unknown' };
    }

    return { os: 'Unknown', osVersion: 'Unknown' };
  }

  private isComplete(ctx: Partial<ClientContext>): ctx is ClientContext {
    return !!(
      ctx.clientType &&
      ctx.deviceId &&
      ctx.appVersion &&
      ctx.screenResolution &&
      ctx.os &&
      ctx.osVersion &&
      ctx.timezone
    );
  }

  private loadFromStorage(): ClientContext | null {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        return null;
      }
      return JSON.parse(raw) as ClientContext;
    } catch {
      return null;
    }
  }

  private saveToStorage(ctx: ClientContext): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
    } catch {
      // ignore storage failures (private mode, quota, etc.)
    }
  }
}
